import React, { useEffect, useState } from 'react';
import * as api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/rbac';
import { pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { IconUsers, IconShoppingBag, IconStar, IconLifeBuoy } from '../components/Icons';

const superStatDefs = [
  { key: 'usersCount', label: 'Brands', Icon: IconUsers, tone: 'ocean' },
  { key: 'ordersCount', label: 'Coupons', Icon: IconShoppingBag, tone: 'slate' },
  { key: 'revenueTotal', label: 'Scans', Icon: IconStar, tone: 'ember' },
  { key: 'openTicketsCount', label: 'Unscanned', Icon: IconLifeBuoy, tone: 'wine' },
];

const brandStatDefs = [
  { key: 'usersCount', label: 'Quota left', Icon: IconUsers, tone: 'ocean' },
  { key: 'ordersCount', label: 'Coupons', Icon: IconShoppingBag, tone: 'slate' },
  { key: 'revenueTotal', label: 'Scans', Icon: IconStar, tone: 'ember' },
  { key: 'openTicketsCount', label: 'Unscanned', Icon: IconLifeBuoy, tone: 'wine' },
];

function StatCard({ label, value, Icon, tone, format }) {
  const display = format ? format(value) : value ?? '—';
  return (
    <div className="col-6 col-md-3">
      <Card className="stat-dash-card h-100 shadow-sm">
        <CardBody className="d-flex gap-3 align-items-center py-3">
          <div className={`stat-dash-icon stat-dash-icon--${tone}`} aria-hidden>
            <Icon size={22} />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="text-secondary small text-uppercase" style={{ letterSpacing: '0.04em', fontSize: '0.68rem' }}>{label}</div>
            <div className="fs-4 fw-semibold text-dark lh-sm font-sans">{display}</div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { hasRole } = useAuth();
  const statDefs = hasRole(ROLES.SUPER_ADMIN) ? superStatDefs : brandStatDefs;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.fetchDashboardCounts();
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={
          hasRole(ROLES.SUPER_ADMIN)
            ? 'Platform overview — brands, coupons, and verifications'
            : 'Your brand — coupon quota, scans, and pending verifications'
        }
      />
      <ErrorBanner message={error} />
      <div className="row g-3">
        {statDefs.map((def) => (
          <StatCard key={def.key} {...def} value={data?.[def.key]} />
        ))}
      </div>
    </>
  );
}
